<template>
  <div class="page-billing-organizations-list flex-grow-1 d-flex flex-column">
    <div class="container-fluid">
      <div class="d-flex align-items-center">
        <h2 class="page-heading mr-auto">Список организаций</h2>
        <button
          class="btn btn-outline-primary mr-2"
          type="button"
          @click="downloadOrganizations"
          :disabled="!displayedOrganizations.length"
        >
          Скачать
        </button>
        <btn-filters
          :data="filtersData"
          endpoint="billing/organizations/list"
          v-model="filtersValue"
        ></btn-filters>
      </div>
    </div>

    <data-table-full-height
      class="flex-grow-1"
      :data="displayedOrganizations"
      :loading="loading"
      :hasMore="false"
      :default-sort="defaultSort"
      @sort-change="handleSortChange"
      @row-click="handleRowClick"
      :row-class-name="getRowClass"
    >
      <el-table-column
        prop="short_name"
        label="Короткое название"
        sortable="custom"
        min-width="220"
      >
        <template slot-scope="{ row }">
          <div class="cell-primary">{{ row.short_name || '—' }}</div>
          <div class="cell-secondary">{{ row.name || '—' }}</div>
        </template>
      </el-table-column>
      <el-table-column
        prop="director_name"
        label="Руководитель"
        sortable="custom"
        min-width="220"
      >
        <template slot-scope="{ row }">
          <div class="cell-primary">{{ row.director_name || '—' }}</div>
          <div class="cell-secondary">
            <template v-if="row.director_phone || row.director_email">
              <strong v-if="row.director_phone">{{ formatDirectorPhone(row.director_phone) }}</strong>
              <span v-if="row.director_phone && row.director_email" class="mx-1">•</span>
              <span v-if="row.director_email">{{ row.director_email }}</span>
            </template>
            <template v-else>
              —
            </template>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="money"
        label="Баланс"
        sortable="custom"
        width="110"
        align="right"
        class-name="column-money"
        label-class-name="column-money"
      >
        <template slot-scope="{ row }">
          <div class="cell-primary">{{ formatMoney(row.money) }}</div>
          <div class="cell-secondary" :class="{'text-muted': !row.discount_active}">{{ row.discount_text }}</div>
        </template>
      </el-table-column>
      <el-table-column
        prop="current_tariff"
        label="Тариф"
        width="140"
        align="center"
        class-name="column-tariff"
        label-class-name="column-tariff"
      >
        <template slot-scope="{ row }">
          <div class="cell-primary">{{ row.current_tariff || '—' }}</div>
          <div class="cell-secondary">{{ row.selected_tariff || '—' }}</div>
        </template>
      </el-table-column>
      <el-table-column
        prop="gmt"
        label="Часовой пояс"
        width="110"
        align="center"
        class-name="column-timezone"
        label-class-name="column-timezone"
      >
        <template slot-scope="{ row }">
          <div class="cell-primary">{{ row.gmt || '—' }}</div>
          <div class="cell-secondary">{{ formatTimezoneCurrentTime(row.gmt) }}</div>
        </template>
      </el-table-column>
      <el-table-column
        prop="machines_with_modem"
        label="Модемы"
        sortable="custom"
        width="100"
        align="center"
        class-name="column-modems"
        label-class-name="column-modems"
      >
        <template slot-scope="{ row }">
          <div class="cell-primary">{{ row.machines_with_modem }}</div>
          <div class="cell-secondary">({{ row.machines_total }})</div>
        </template>
      </el-table-column>
      <el-table-column
        prop="last_modem_online"
        label="Последний онлайн модема"
        sortable="custom"
        width="220"
        align="center"
      >
        <template slot-scope="{ row }">
          <div class="cell-primary font-weight-normal">{{ formatLastModemOnline(row.last_modem_online) }}</div>
        </template>
      </el-table-column>
    </data-table-full-height>
  </div>
</template>

<script>
import api from '@/api'
import BtnFilters from '@/components/btn-filters.vue'
import DataTableFullHeight from '@/components/data-table-full-height.vue'
import filters from '@/filters'
import moment from 'moment'

const DEFAULT_MIN_MODEMS = 0
const DEFAULT_MAX_MODEMS = 1000
const {formatDate} = filters

export default {
  metaInfo: {title: 'Список организаций'},
  components: {
    DataTableFullHeight,
    BtnFilters,
  },
  data () {
    return {
      organizations: [],
      loading: false,
      sort: {
        prop: 'short_name',
        order: 'ascending',
      },
      filtersValue: {
        modems: {
          min: DEFAULT_MIN_MODEMS,
          max: DEFAULT_MAX_MODEMS,
        },
      },
    }
  },
  computed: {
    activeFilters () {
      const filters = this.filtersValue || {}
      const modems = filters.modems || {}

      return {
        current_tariff: Array.isArray(filters.current_tariff) ? filters.current_tariff : [],
        selected_tariff: Array.isArray(filters.selected_tariff) ? filters.selected_tariff : [],
        money: filters.money || {},
        modems: {
          min: this.getModemFilterValue(modems, 'min', DEFAULT_MIN_MODEMS),
          max: this.getModemFilterValue(modems, 'max', DEFAULT_MAX_MODEMS),
        },
      }
    },
    filteredOrganizations () {
      const filters = this.activeFilters
      const balanceMin = this.parseNumber(filters.money.min)
      const balanceMax = this.parseNumber(filters.money.max)
      const modemMin = this.parsePositiveInteger(filters.modems.min)
      const modemMax = this.parsePositiveInteger(filters.modems.max)

      return this.organizations.filter((organization) => {
        if (filters.current_tariff.length && !filters.current_tariff.includes(organization.current_tariff_code)) {
          return false
        }

        if (filters.selected_tariff.length && !filters.selected_tariff.includes(organization.selected_tariff_code)) {
          return false
        }

        const balance = this.toNumber(organization.money)

        if (balanceMin !== null && balance < balanceMin) {
          return false
        }

        if (balanceMax !== null && balance > balanceMax) {
          return false
        }

        const modemCount = Number(organization.machines_with_modem) || 0

        if (modemMin !== null && modemCount < modemMin) {
          return false
        }

        if (modemMax !== null && modemCount > modemMax) {
          return false
        }

        return true
      })
    },
    displayedOrganizations () {
      const organizations = this.filteredOrganizations.slice()

      if (!this.sort.prop || !this.sort.order) {
        return organizations
      }

      const {prop, order} = this.sort
      const direction = order === 'descending' ? -1 : 1
      const numericProps = ['money', 'machines_with_modem']
      const dateProps = ['last_modem_online']

      return organizations.sort((a, b) => {
        const aValue = a[prop]
        const bValue = b[prop]

        if (numericProps.includes(prop)) {
          const aNumber = this.toNumber(aValue)
          const bNumber = this.toNumber(bValue)

          if (aNumber === bNumber) {
            return 0
          }
          return aNumber > bNumber ? direction : -direction
        }

        if (dateProps.includes(prop)) {
          let aTime = this.toTimestamp(aValue)
          let bTime = this.toTimestamp(bValue)

          const fallback = order === 'descending' ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY

          if (aTime === null) {
            aTime = fallback
          }

          if (bTime === null) {
            bTime = fallback
          }

          if (aTime === bTime) {
            return 0
          }

          return aTime > bTime ? direction : -direction
        }

        const aString = (aValue || '').toString()
        const bString = (bValue || '').toString()
        const result = aString.localeCompare(bString, 'ru', {sensitivity: 'base'})
        return result * direction
      })
    },
    defaultSort () {
      if (!this.sort.prop || !this.sort.order) {
        return {}
      }

      return {
        prop: this.sort.prop,
        order: this.sort.order,
      }
    },
    currentTariffOptions () {
      return this.buildTariffOptions('current_tariff_code', 'current_tariff')
    },
    selectedTariffOptions () {
      return this.buildTariffOptions('selected_tariff_code', 'selected_tariff')
    },

    balanceRange () {
      const balances = this.organizations
        .map((organization) => {
          const value = Number(organization.money)
          return Number.isFinite(value) ? value : null
        })
        .filter((value) => value !== null)

      if (!balances.length) {
        return {min: 0, max: 0}
      }

      return {
        min: Math.min(...balances),
        max: Math.max(...balances),
      }
    },
    filtersDefinitions () {
      const definitions = {}

      if (this.currentTariffOptions.length) {
        definitions.current_tariff = {
          label: 'Действующий тариф',
          type: 'checkboxes',
          params: {
            options: this.currentTariffOptions,
          },
        }
      }

      if (this.selectedTariffOptions.length) {
        definitions.selected_tariff = {
          label: 'Выбранный тариф',
          type: 'checkboxes',
          params: {
            options: this.selectedTariffOptions,
          },
        }
      }

      definitions.money = {
        label: 'Баланс',
        type: 'range',
        params: {
          range: {
            min: this.balanceRange.min,
            max: this.balanceRange.max,
          },
        },
      }

      definitions.modems = {
        label: 'Модемы',
        type: 'range',
        params: {
          range: {
            min: DEFAULT_MIN_MODEMS,
            max: DEFAULT_MAX_MODEMS,
          },
        },
      }

      return definitions
    },
    filtersData () {
      return {
        filters: this.filtersDefinitions,
        total: this.filteredOrganizations.length,
      }
    },
  },
  watch: {
    filtersValue: {
      handler (value) {
        if (!value || Object.keys(value).length === 0) {
          this.$nextTick(() => {
            this.filtersValue = this.getDefaultFilters()
          })
          return
        }

        if (!value.modems) {
          this.$nextTick(() => {
            this.$set(this.filtersValue, 'modems', this.getDefaultModemFilter())
          })
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created () {
    this.fetchOrganizations()
  },
  methods: {
    async fetchOrganizations () {
      this.loading = true
      try {
        const {data} = await api.get('billing/organizations/list')
        this.organizations = data.data || []
      } finally {
        this.loading = false
      }
    },
    formatDirectorPhone (value) {
      if (!value) {
        return '—'
      }

      const digits = value.toString().replace(/\D/g, '')

      if (!digits) {
        return value
      }

      let normalized = digits

      if (normalized.length === 11 && normalized.startsWith('8')) {
        normalized = `7${normalized.slice(1)}`
      }

      if (normalized.length === 10) {
        normalized = `7${normalized}`
      }

      if (normalized.length !== 11) {
        return value
      }

      const country = normalized.slice(0, 1)
      const part1 = normalized.slice(1, 4)
      const part2 = normalized.slice(4, 7)
      const part3 = normalized.slice(7, 9)
      const part4 = normalized.slice(9, 11)

      return `+${country} (${part1}) ${part2}-${part3}-${part4}`
    },
    formatLastModemOnline (value) {
      if (!value) {
        return '—'
      }

      return formatDate(value, 'DD.MM.YYYY HH:mm:ss')
    },
    formatTimezoneCurrentTime (gmt) {
      const offset = this.getTimezoneOffsetMinutes(gmt)

      if (offset === null) {
        return '—'
      }

      return moment().utcOffset(offset).format('HH:mm')
    },
    toTimestamp (value) {
      if (!value) {
        return null
      }

      const timestamp = new Date(value).getTime()

      return Number.isNaN(timestamp) ? null : timestamp
    },
    downloadOrganizations () {
      const organizations = this.displayedOrganizations

      if (!organizations.length) {
        return
      }

      const headers = [
        'Короткое название',
        'Полное название',
        'Руководитель',
        'Телефон',
        'Email',
        'Баланс',
        'Скидка',
        'Действующий тариф',
        'Выбранный тариф',
        'Часовой пояс',
        'Модемы с автоматом',
        'Всего автоматов',
        'Последний онлайн модема',
      ]

      const rows = organizations.map((organization) => [
        organization.short_name || '—',
        organization.name || '—',
        organization.director_name || '—',
        organization.director_phone ? this.formatDirectorPhone(organization.director_phone) : '—',
        organization.director_email || '—',
        this.formatMoney(organization.money),
        organization.discount_text || '—',
        organization.current_tariff || '—',
        organization.selected_tariff || '—',
        organization.gmt || '—',
        organization.machines_with_modem !== undefined ? organization.machines_with_modem : '—',
        organization.machines_total !== undefined ? organization.machines_total : '—',
        this.formatLastModemOnline(organization.last_modem_online),
      ])

      const csvContent = [headers, ...rows]
        .map((row) => row.map(this.escapeCsvValue).join(';'))
        .join('\n')

      const blob = new Blob([`\ufeff${csvContent}`], {type: 'text/csv;charset=utf-8;'})
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'organizations.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    escapeCsvValue (value) {
      const stringValue = value === null || value === undefined ? '' : value.toString()
      const needsQuotes = /[";\n]/.test(stringValue)
      const escapedValue = stringValue.replace(/"/g, '""')
      return needsQuotes ? `"${escapedValue}"` : escapedValue
    },
    handleSortChange ({prop, order}) {
      if (!order) {
        this.sort = {
          prop: null,
          order: null,
        }
        return
      }

      this.sort = {prop, order}
    },
    handleRowClick (row) {
      if (row && row.id) {
        this.$router.push(`/billing/organization/${row.id}/info`)
      }
    },
    getRowClass () {
      return 'clickable-row'
    },
    formatMoney (value) {
      if (value === null || value === undefined) {
        return '—'
      }

      try {
        return Number(value).toLocaleString('ru-RU', {
          style: 'currency',
          currency: 'RUB',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      } catch (e) {
        return `${value} ₽`
      }
    },
    toNumber (value) {
      if (typeof value === 'number') {
        return value
      }

      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : 0
    },
    parseNumber (value) {
      if (value === null || value === undefined || value === '') {
        return null
      }

      const number = Number(value)
      return Number.isFinite(number) ? number : null
    },
    parsePositiveInteger (value) {
      if (value === null || value === undefined || value === '') {
        return null
      }

      const number = Number(value)

      if (!Number.isFinite(number)) {
        return null
      }

      if (number < 0) {
        return 0
      }

      return Math.floor(number)
    },
    getModemFilterValue (modems, key, fallback) {
      if (modems && Object.prototype.hasOwnProperty.call(modems, key)) {
        return modems[key]
      }

      return fallback
    },
    buildTariffOptions (codeKey, labelKey) {
      const counts = {}

      this.organizations.forEach((organization) => {
        const code = organization[codeKey]
        const label = organization[labelKey]

        if (!code || !label) {
          return
        }

        if (!counts[code]) {
          counts[code] = {
            value: code,
            label,
            count: 0,
          }
        }

        counts[code].count += 1
      })

      return Object.values(counts).sort((a, b) => a.label.localeCompare(b.label, 'ru', {sensitivity: 'base'}))
    },
    getDefaultModemFilter () {
      return {
        min: DEFAULT_MIN_MODEMS,
        max: DEFAULT_MAX_MODEMS,
      }
    },
    getDefaultFilters () {
      return {
        modems: this.getDefaultModemFilter(),
      }
    },
    getTimezoneOffsetMinutes (gmt) {
      if (gmt === null || gmt === undefined || gmt === '') {
        return null
      }

      if (typeof gmt === 'number' && Number.isFinite(gmt)) {
        return gmt
      }

      const stringValue = gmt.toString().trim()

      if (!stringValue) {
        return null
      }

      const normalized = stringValue.replace(/^GMT/i, '')
      const match = normalized.match(/^([+-]?)(\d{1,2})(?::?(\d{2}))?$/)

      if (!match) {
        return null
      }

      const sign = match[1] === '-' ? -1 : 1
      const hours = parseInt(match[2], 10)
      const minutes = match[3] ? parseInt(match[3], 10) : 0

      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return null
      }

      return sign * (hours * 60 + minutes)
    },
  },
}
</script>

<style lang="scss" scoped>
.page-billing-organizations-list {
  :deep(.column-money) {
    width: 100px !important;
    max-width: 100px;

    .cell {
      white-space: nowrap;
    }
  }

  :deep(.column-tariff) {
    width: 128px !important;
    max-width: 128px;

    .cell {
      white-space: normal;
      word-break: break-word;
    }
  }

  :deep(.column-timezone) {
    width: 110px !important;
    max-width: 110px;

    .cell {
      white-space: normal;
    }
  }

  :deep(.column-modems) {
    width: 100px !important;
    max-width: 100px;

    .cell {
      white-space: normal;
    }
  }

  :deep(.column-modem-last-online) {
    width: 220px !important;
    max-width: 220px;

    .cell {
      white-space: normal;
      word-break: break-word;
    }
  }

  .cell-primary {
    font-weight: 600;
    line-height: 1.2;
  }

  .cell-secondary {
    display: block;
    font-size: 12px;
    color: #6c757d;
    line-height: 1.2;
  }

  .text-muted {
    color: #adb5bd;
  }

  :deep(.el-table .cell-secondary) {
    margin-top: 2px;
  }

  :deep(.el-table .clickable-row) {
    cursor: pointer;
  }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/pages/billing/organizations/list.vue